"use client";

import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/use-debounce";
import { useMounted } from "@/hooks/use-mounted";

interface SearchFilterProps {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function SearchFilter({
  className = "",
  value,
  onChange,
  placeholder = "Search listings...",
}: SearchFilterProps) {
  const [searchTerm, setSearchTerm] = useState(value);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isMounted = useMounted();

  useEffect(() => {
    if (isMounted) {
      onChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onChange, isMounted]);

  const handleClear = () => {
    setSearchTerm("");
  };

  return (
    <div className={`relative ${className}`}>
      <Search
        className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="h-10 pr-9 pl-9 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none"
        aria-label={placeholder}
      />
      {searchTerm && (
        <Button
          type="button"
          variant="link"
          className="absolute top-1/2 right-2 size-6 -translate-y-1/2 p-0"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
